/**
 * Hekaya Outlet — Smart Predictive Search Engine
 * Native, resilient, client-side catalog search with Arabic normalization,
 * dialect mapping, multi-tier ranking, keyboard navigation, and touch support.
 */

(function () {
  'use strict';

  // Global cache for catalog products & collections
  window.__hekayaCatalog = window.__hekayaCatalog || null;
  window.__hekayaCollections = window.__hekayaCollections || null;
  let catalogLoadingPromise = null;
  let collectionsLoadingPromise = null;

  // --- 1. Arabic Text Normalization & Dialect Engine ---

  function normalizeArabic(text) {
    if (!text) return '';
    let s = String(text).trim();
    // Strip diacritics / harakat
    s = s.replace(/[\u064B-\u065F\u0670]/g, '');
    // Strip tatweel / kashida
    s = s.replace(/\u0640/g, '');
    // Normalize alefs (أ, إ, آ, ٱ -> ا)
    s = s.replace(/[أإآٱ]/g, 'ا');
    // Normalize taa marbuta to haa (ة -> ه) for matching
    s = s.replace(/ة/g, 'ه');
    // Normalize yaa / alef maqsura (ى -> ي)
    s = s.replace(/ى/g, 'ي');
    // Remove symbols/punctuation, preserve Arabic letters, English letters, digits
    s = s.replace(/[^\w\s\u0600-\u06FF]/gi, ' ');
    return s.replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function stripAlifLam(word) {
    if (!word) return '';
    if (word.startsWith('ال') && word.length > 3) {
      return word.substring(2);
    }
    return word;
  }

  // Synonym & Dialect Groups
  const SYNONYM_GROUPS = [
    ['بلوزة', 'بلوزه', 'بلايز', 'تيشيرت', 'تي شيرت', 'تيشرت', 't-shirt', 'tshirt'],
    ['جاكيت', 'جاكيتات', 'كوت', 'كوتات', 'jacket'],
    ['هودي', 'هوديز', 'سويتشيرت', 'سويت شيرت', 'hoodie'],
    ['بيجامة', 'بيجاما', 'بجامة', 'بجامات', 'pajama', 'pyjama'],
    ['بنطلون', 'بنطال', 'بناطيل', 'سروال', 'جينز', 'jeans', 'pants'],
    ['ولادي', 'اولادي', 'أولادي', 'صبياني', 'boys', 'boy'],
    ['بناتي', 'بنات', 'صبايا', 'girls', 'girl'],
    ['نسائي', 'نسواني', 'ستاتي', 'women', 'ladies'],
    ['رجالي', 'رجال', 'men', 'man'],
    ['حذاء', 'احذية', 'أحذية', 'كندرة', 'كنادر', 'بوط', 'بوات', 'شوز', 'شوزات', 'sneakers', 'shoes'],
    ['افرهول', 'أفرهول', 'افرهولات', 'أفرهولات', 'كارترز', 'romper', 'sleepsuit'],
    ['شنطة', 'شنط', 'شناتي', 'حقيبة', 'حقائب', 'باغ', 'bag', 'handbag'],
    ['عطر', 'عطور', 'معطر', 'معطرات', 'ميست', 'perfume', 'mist', 'body mist'],
    ['مكياج', 'ميك اب', 'ميكاب', 'تجميل', 'beauty', 'makeup'],
    ['شال', 'شالات', 'توربان', 'ايشارب', 'scarf', 'shawl'],
    ['فستان', 'فساتين', 'دريس', 'dress'],
    ['طقم', 'اطقم', 'أطقم', 'تريننج', 'tracksuit', 'set']
  ];

  // Pre-normalized synonym lookup map
  const SYNONYM_MAP = new Map();
  SYNONYM_GROUPS.forEach(group => {
    const normGroup = group.map(w => normalizeArabic(w));
    normGroup.forEach(word => {
      if (!SYNONYM_MAP.has(word)) {
        SYNONYM_MAP.set(word, new Set());
      }
      normGroup.forEach(syn => {
        if (syn !== word) SYNONYM_MAP.get(word).add(syn);
      });
    });
  });

  function getSynonyms(word) {
    const norm = normalizeArabic(word);
    const set = SYNONYM_MAP.get(norm);
    const stripped = stripAlifLam(norm);
    const setStripped = SYNONYM_MAP.get(stripped);
    const result = new Set();
    if (set) set.forEach(s => result.add(s));
    if (setStripped) setStripped.forEach(s => result.add(s));
    return Array.from(result);
  }

  // Conversational prefix cleaner
  const CONVERSATIONAL_REGEX = /^(بدي اشوف|بدي اشتري|بدي|ورجيني|فرجيني|دورلي على|ابحث عن|لو سمحت بدي|شو في عندكم|في عندكم|عندكم)\s+/i;
  function cleanConversationalPrefix(text) {
    if (!text) return '';
    return text.replace(CONVERSATIONAL_REGEX, '').trim();
  }

  // Format JOD Currency
  function formatJOD(price) {
    const num = parseFloat(price);
    if (isNaN(num)) return '0.00 د.أ';
    return num.toFixed(2) + ' د.أ';
  }

  // --- 2. Dynamic Catalog & Collections Loader ---

  async function loadCatalog() {
    if (window.__hekayaCatalog && window.__hekayaCatalog.length > 0) {
      return window.__hekayaCatalog;
    }
    if (catalogLoadingPromise) return catalogLoadingPromise;

    catalogLoadingPromise = (async () => {
      try {
        const products = [];
        const seenIds = new Set();
        let page = 1;
        while (true) {
          const res = await fetch(`/products.json?limit=250&page=${page}`);
          if (!res.ok) break;
          const data = await res.json();
          if (!data || !Array.isArray(data.products)) break;
          for (const p of data.products) {
            if (!seenIds.has(p.id)) {
              seenIds.add(p.id);
              // Precompute normalized searchable strings
              p._normTitle = normalizeArabic(p.title || '');
              p._normType = normalizeArabic(p.product_type || '');
              p._normTags = normalizeArabic(Array.isArray(p.tags) ? p.tags.join(' ') : (p.tags || ''));
              p._isMixOffer = Array.isArray(p.tags) ? p.tags.includes('mix-offer') : String(p.tags || '').includes('mix-offer');
              products.push(p);
            }
          }
          if (data.products.length < 250) break;
          page++;
        }
        window.__hekayaCatalog = products;
        return products;
      } catch (err) {
        console.warn('Error fetching catalog products:', err);
        return window.__hekayaCatalog || [];
      } finally {
        catalogLoadingPromise = null;
      }
    })();

    return catalogLoadingPromise;
  }

  // --- 2.2 Collection Parent Hierarchy & Department Clarity Map ---
  const COLLECTION_PARENT_MAP = {
    'men-shirts': { parent: 'الملابس الرجالية', label: 'رجالي' },
    'men-jeans': { parent: 'الملابس الرجالية', label: 'رجالي' },
    'men-linen': { parent: 'الملابس الرجالية', label: 'رجالي' },
    'men-tracksuits': { parent: 'الملابس الرجالية', label: 'رجالي' },
    'men-underwear': { parent: 'الملابس الرجالية', label: 'رجالي' },
    'men-formal-shoes': { parent: 'الأحذية الرجالية', label: 'رجالي' },
    'women-dresses': { parent: 'الملابس النسائية', label: 'نسائي' },
    'women-sneakers': { parent: 'أحذية نسائية وأطفال', label: 'نسائي' },
    'women-heels': { parent: 'أحذية نسائية وأطفال', label: 'نسائي' },
    'kids-hoodies': { parent: 'ملابس الأطفال', label: 'أطفال' },
    'kids-tracksuits': { parent: 'ملابس الأطفال', label: 'أطفال' },
    'kids-pajamas': { parent: 'ملابس الأطفال', label: 'أطفال' },
    'kids-knitwear': { parent: 'ملابس الأطفال', label: 'أطفال' },
    'baby-carters-boys': { parent: 'ملابس البيبي', label: 'بيبي ولادي' },
    'baby-carters-girls': { parent: 'ملابس البيبي', label: 'بيبي بناتي' },
    'baby-rompers': { parent: 'ملابس البيبي', label: 'بيبي' },
    'baby-essentials': { parent: 'ملابس البيبي', label: 'مستلزمات بيبي' },
    'baby-feeding': { parent: 'ملابس ومستلزمات البيبي', label: 'تغذية وعناية' },
    'lingerie-pajamas': { parent: 'اللانجيري', label: 'لانجيري' },
    'lingerie-robes': { parent: 'اللانجيري', label: 'لانجيري' },
    'beauty-eyeshadow': { parent: 'المكياج والتجميل (الإكسسوارات)', label: 'مكياج' },
    'bags-handbags': { parent: 'الشنطات والحقائب (الإكسسوارات)', label: 'حقائب' },
    'scarves-shawls': { parent: 'الشالات والتوربان (الإكسسوارات)', label: 'شالات' },
    'perfumes-fragrances': { parent: 'العطور ومعطرات الجسم (الإكسسوارات)', label: 'عطور' },
    'mix-offer': { parent: 'عرض 2 بـ 15 د.أ', label: 'عرض خاص' },
    'women': { parent: '', label: 'رئيسي' },
    'men': { parent: '', label: 'رئيسي' },
    'kids': { parent: '', label: 'رئيسي' },
    'baby': { parent: '', label: 'رئيسي' },
    'lingerie': { parent: '', label: 'رئيسي' },
    'accessories': { parent: '', label: 'رئيسي' },
    'beauty': { parent: 'الإكسسوارات', label: 'تجميل' },
    'bags': { parent: 'الإكسسوارات', label: 'شنط' },
    'scarves': { parent: 'الإكسسوارات', label: 'شالات' },
    'perfumes': { parent: 'الإكسسوارات', label: 'عطور' },
    'women-shoes': { parent: '', label: 'رئيسي' },
    'men-shoes': { parent: '', label: 'رئيسي' }
  };

  function getCollectionDisplayInfo(col) {
    const info = COLLECTION_PARENT_MAP[col.handle] || { parent: '', label: '' };
    const parent = info.parent || '';
    const label = info.label || '';
    let displayTitle = col.title || '';
    if (parent && label && !displayTitle.includes(label)) {
      displayTitle = `${col.title} (${label})`;
    }
    return {
      title: displayTitle,
      parent: parent,
      label: label || 'متوفر'
    };
  }

  async function loadCollections() {
    if (window.__hekayaCollections && window.__hekayaCollections.length > 0) {
      return window.__hekayaCollections;
    }
    if (collectionsLoadingPromise) return collectionsLoadingPromise;

    collectionsLoadingPromise = (async () => {
      try {
        const collections = [];
        const seenIds = new Set();
        let page = 1;
        while (true) {
          const res = await fetch(`/collections.json?limit=50&page=${page}`);
          if (!res.ok) break;
          const data = await res.json();
          if (!data || !Array.isArray(data.collections)) break;
          for (const c of data.collections) {
            if (!seenIds.has(c.id)) {
              seenIds.add(c.id);
              c._normTitle = normalizeArabic(c.title || '');
              const cInfo = COLLECTION_PARENT_MAP[c.handle];
              c._normParent = cInfo && cInfo.parent ? normalizeArabic(cInfo.parent) : '';
              c._normLabel = cInfo && cInfo.label ? normalizeArabic(cInfo.label) : '';
              collections.push(c);
            }
          }
          if (data.collections.length < 50) break;
          page++;
        }
        window.__hekayaCollections = collections;
        return collections;
      } catch (err) {
        console.warn('Error fetching collections:', err);
        return window.__hekayaCollections || [];
      } finally {
        collectionsLoadingPromise = null;
      }
    })();

    return collectionsLoadingPromise;
  }

  // --- 3. Smart Search Matching & Relevance Scoring ---

  function scoreProduct(p, query, queryWords, queryNorm, synonymsMap) {
    const title = p._normTitle;
    const type = p._normType;
    const tags = p._normTags;
    let score = 0;

    // 1. Exact title match
    if (title === queryNorm) {
      score += 1000;
    } else if (title.startsWith(queryNorm)) {
      score += 700;
    } else if (title.includes(queryNorm)) {
      score += 450;
    }

    // 2. Word by word matching
    let allWordsFound = true;
    for (const w of queryWords) {
      const stripped = stripAlifLam(w);
      const syns = synonymsMap.get(w) || [];
      let wordFound = false;

      if (title.includes(w)) {
        score += 140;
        wordFound = true;
      } else if (stripped !== w && title.includes(stripped)) {
        score += 120;
        wordFound = true;
      } else {
        // Check prefix match in title words
        const titleWords = title.split(' ');
        const prefixMatch = titleWords.some(tw => tw.startsWith(w) || (stripped && tw.startsWith(stripped)));
        if (prefixMatch) {
          score += 90;
          wordFound = true;
        } else {
          // Check synonyms
          const synMatch = syns.some(s => title.includes(s));
          if (synMatch) {
            score += 85;
            wordFound = true;
          }
        }
      }

      // Check product_type & tags if not in title
      if (!wordFound) {
        if (type.includes(w) || (stripped && type.includes(stripped))) {
          score += 60;
          wordFound = true;
        } else if (tags.includes(w) || (stripped && tags.includes(stripped))) {
          score += 40;
          wordFound = true;
        } else if (syns.some(s => type.includes(s) || tags.includes(s))) {
          score += 35;
          wordFound = true;
        }
      }

      if (!wordFound) {
        allWordsFound = false;
      }
    }

    // If query has multiple words and all are found, provide significant cohesion boost
    if (queryWords.length > 1 && allWordsFound) {
      score += 300;
    }

    // 3. Strict Gender & Category Guardrails
    const queryHasBanat = queryNorm.includes('بنات') || queryNorm.includes('بناتي');
    const queryHasWalad = queryNorm.includes('ولاد') || queryNorm.includes('ولادي') || queryNorm.includes('صبيان');

    if (queryHasBanat) {
      const itemHasBanat = title.includes('بنات') || title.includes('بناتي') || tags.includes('بنات');
      const itemHasWalad = title.includes('ولاد') || title.includes('ولادي') || tags.includes('ولاد');
      if (itemHasBanat) {
        score += 400;
      }
      if (itemHasWalad && !itemHasBanat) {
        score -= 600; // Heavily penalize boys items when searching for girls
      }
    }

    if (queryHasWalad) {
      const itemHasWalad = title.includes('ولاد') || title.includes('ولادي') || tags.includes('ولاد');
      const itemHasBanat = title.includes('بنات') || title.includes('بناتي') || tags.includes('بنات');
      if (itemHasWalad) {
        score += 400;
      }
      if (itemHasBanat && !itemHasWalad) {
        score -= 600; // Heavily penalize girls items when searching for boys
      }
    }

    // Specific item boosts
    if (queryNorm.includes('هودي') || queryNorm.includes('hoodie')) {
      if (title.includes('هودي') || title.includes('hoodie')) {
        score += 300;
      }
    }

    if (queryNorm.includes('بيجام') || queryNorm.includes('بجام')) {
      if (title.includes('بيجام') || title.includes('بجام')) {
        score += 300;
      }
    }

    // In-stock availability tie breaker (+10 points only if already matched)
    const isAvailable = p.variants && p.variants.some(v => v.available !== false);
    if (isAvailable && score > 0) {
      score += 10;
    }

    return score > 0 ? score : 0;
  }

  // Ranks the whole catalog for a term with the same scoring as the predictive panel.
  // Returns null when the catalog could not be loaded, so callers can keep their fallback.
  async function rankProducts(term) {
    const catalog = await loadCatalog();
    if (!catalog || catalog.length === 0) return null;

    const cleanTerm = cleanConversationalPrefix(String(term || '').trim());
    const queryNorm = normalizeArabic(cleanTerm);
    const queryWords = queryNorm.split(/\s+/).filter(Boolean);
    if (queryWords.length === 0) return [];

    const synonymsMap = new Map();
    queryWords.forEach(w => {
      synonymsMap.set(w, getSynonyms(w));
    });

    const scoredProducts = [];
    for (const p of catalog) {
      const score = scoreProduct(p, cleanTerm, queryWords, queryNorm, synonymsMap);
      if (score > 0) {
        const available = Array.isArray(p.variants) && p.variants.some(v => v.available !== false);
        scoredProducts.push({ product: p, score, available });
      }
    }

    scoredProducts.sort((a, b) => b.score - a.score);
    return scoredProducts;
  }

  function matchCollections(collections, queryWords, queryNorm, synonymsMap) {
    const matched = [];
    for (const c of collections) {
      const title = c._normTitle;
      const parent = c._normParent || '';
      const label = c._normLabel || '';
      let score = 0;

      if (title === queryNorm) {
        score += 500;
      } else if (title.includes(queryNorm)) {
        score += 300;
      }

      for (const w of queryWords) {
        const stripped = stripAlifLam(w);
        const syns = synonymsMap.get(w) || [];

        if (title.includes(w)) {
          score += 100;
        } else if (stripped !== w && title.includes(stripped)) {
          score += 80;
        } else if (syns.some(s => title.includes(s))) {
          score += 70;
        }

        // Parent department and label matching (e.g. searching "اطفال" matches kids-pajamas)
        if (parent && (parent.includes(w) || (stripped !== w && parent.includes(stripped)))) {
          score += 60;
        }
        if (label && (label.includes(w) || (stripped !== w && label.includes(stripped)))) {
          score += 60;
        }
      }

      // Gender separation
      const queryHasBanat = queryNorm.includes('بنات') || queryNorm.includes('بناتي');
      const queryHasWalad = queryNorm.includes('ولاد') || queryNorm.includes('ولادي');
      if (queryHasBanat && (title.includes('ولاد') || title.includes('رجال') || label.includes('ولاد') || label.includes('رجال')) && !title.includes('بنات')) {
        score = 0;
      }
      if (queryHasWalad && (title.includes('بنات') || title.includes('نساء') || label.includes('بنات') || label.includes('نسائي')) && !title.includes('ولاد')) {
        score = 0;
      }

      if (score > 50) {
        matched.push({ collection: c, score });
      }
    }

    matched.sort((a, b) => b.score - a.score);
    return matched.slice(0, 3).map(m => m.collection);
  }

  function extractQuerySuggestions(matchedProducts, rawQuery, queryNorm) {
    const suggestions = [];
    const seen = new Set();

    // 1. Natural phrase completions from matched product titles
    for (const p of matchedProducts) {
      const rawTitle = p.title.trim();
      const normTitle = p._normTitle;

      // Split raw title into words
      const words = rawTitle.split(/\s+/);
      if (words.length >= 2) {
        // 2-word phrase
        const twoWord = words.slice(0, 2).join(' ');
        const normTwo = normalizeArabic(twoWord);
        if (normTwo.includes(queryNorm) || queryNorm.includes(normTwo) || normTwo.startsWith(queryNorm)) {
          if (!seen.has(normTwo)) {
            seen.add(normTwo);
            suggestions.push(twoWord);
          }
        }

        // 3-word phrase if available
        if (words.length >= 3) {
          const threeWord = words.slice(0, 3).join(' ');
          const normThree = normalizeArabic(threeWord);
          if (normThree.includes(queryNorm) || normThree.startsWith(queryNorm)) {
            if (!seen.has(normThree)) {
              seen.add(normThree);
              suggestions.push(threeWord);
            }
          }
        }
      }
      if (suggestions.length >= 4) break;
    }

    // If less than 2 suggestions and we have product types, use them
    if (suggestions.length < 4) {
      for (const p of matchedProducts) {
        if (p.product_type && p.product_type.trim()) {
          const normType = p._normType;
          if (!seen.has(normType)) {
            seen.add(normType);
            suggestions.push(p.product_type.trim());
          }
        }
        if (suggestions.length >= 4) break;
      }
    }

    return suggestions.slice(0, 4);
  }

  // --- 4. Predictive Search Component Class ---

  class HekayaSmartSearch {
    constructor(options) {
      this.input = options.input;
      this.form = options.form || (this.input ? this.input.form : null);
      this.container = options.container;
      this.mode = options.mode || 'dropdown'; // 'dropdown' or 'inline'
      this.onSelect = options.onSelect || null;
      this.clearBtn = options.clearBtn || null;

      this.debounceTimer = null;
      this.activeController = null;
      this.highlightedIndex = -1;
      this.interactiveElements = [];

      this.init();
    }

    init() {
      if (!this.input || !this.container) return;

      // Preload catalog data in background on first user interaction
      const preloadData = () => {
        loadCatalog().catch(() => {});
        loadCollections().catch(() => {});
      };

      this.input.addEventListener('focus', preloadData, { once: true });
      this.input.addEventListener('mouseenter', preloadData, { once: true });

      // Input listening
      this.input.addEventListener('input', () => {
        const val = this.input.value;
        if (this.clearBtn) {
          this.clearBtn.style.display = val.length > 0 ? 'block' : 'none';
        }
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          this.executeSearch(val);
        }, 180);
      });

      // Keyboard navigation
      this.input.addEventListener('keydown', (e) => this.handleKeyDown(e));

      // Click outside listener
      document.addEventListener('click', (e) => {
        if (!this.container.contains(e.target) && !this.input.contains(e.target)) {
          this.closeDropdown();
        }
      });

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', () => {
          this.input.value = '';
          this.clearBtn.style.display = 'none';
          this.closeDropdown();
          this.input.focus();
        });
      }
    }

    handleKeyDown(e) {
      if (this.container.style.display === 'none' || this.interactiveElements.length === 0) {
        if (e.key === 'Escape') {
          this.closeDropdown();
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.highlightedIndex++;
        if (this.highlightedIndex >= this.interactiveElements.length) {
          this.highlightedIndex = 0;
        }
        this.updateHighlight();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.highlightedIndex--;
        if (this.highlightedIndex < -1) {
          this.highlightedIndex = this.interactiveElements.length - 1;
        }
        this.updateHighlight();
      } else if (e.key === 'Enter') {
        if (this.highlightedIndex >= 0 && this.interactiveElements[this.highlightedIndex]) {
          e.preventDefault();
          this.interactiveElements[this.highlightedIndex].click();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.closeDropdown();
      }
    }

    updateHighlight() {
      this.interactiveElements.forEach((el, idx) => {
        if (idx === this.highlightedIndex) {
          el.classList.add('is-highlighted');
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          el.classList.remove('is-highlighted');
        }
      });

      if (this.highlightedIndex === -1) {
        this.input.focus();
      }
    }

    closeDropdown() {
      if (this.container) {
        this.container.style.display = 'none';
        this.container.innerHTML = '';
      }
      this.highlightedIndex = -1;
      this.interactiveElements = [];
    }

    async executeSearch(rawQuery) {
      const cleanTerm = cleanConversationalPrefix(rawQuery);
      if (!cleanTerm || cleanTerm.trim().length < 2) {
        this.closeDropdown();
        return;
      }

      if (this.activeController) {
        this.activeController.abort();
      }
      this.activeController = new AbortController();
      const signal = this.activeController.signal;

      // Show loading spinner
      this.container.style.display = 'flex';
      this.container.innerHTML = `
        <div class="fudge-smart-search__loading">
          <div class="fudge-smart-search__spinner"></div>
          <span>جاري مطابقة المنتجات والأسعار من متجر حكاية...</span>
        </div>
      `;

      try {
        const catalog = await loadCatalog();
        if (signal.aborted || this.input.value.trim() !== rawQuery.trim()) return;

        const queryNorm = normalizeArabic(cleanTerm);
        const queryWords = queryNorm.split(/\s+/).filter(Boolean);

        // Build synonyms map for query words
        const synonymsMap = new Map();
        queryWords.forEach(w => {
          synonymsMap.set(w, getSynonyms(w));
        });

        // 1. Score and rank all products
        const scoredProducts = [];
        for (const p of catalog) {
          const score = scoreProduct(p, cleanTerm, queryWords, queryNorm, synonymsMap);
          if (score > 0) {
            scoredProducts.push({ product: p, score });
          }
        }

        // Sort descending by relevance score
        scoredProducts.sort((a, b) => b.score - a.score);
        const topProducts = scoredProducts.slice(0, 6).map(item => item.product);

        // 2. Related collections are not shown while searching; results list products only
        const matchedCollections = [];

        // 3. Extract query suggestions
        const querySuggestions = extractQuerySuggestions(topProducts, cleanTerm, queryNorm);

        // Render UI
        this.renderResults(cleanTerm, querySuggestions, matchedCollections, topProducts, scoredProducts.length);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Smart search notice:', err);
          this.container.innerHTML = `
            <div class="fudge-smart-search__empty">
              <p class="fudge-smart-search__empty-title">تعذر إكمال البحث التنبؤي مؤقتاً</p>
              <p class="fudge-smart-search__empty-desc">يمكنك الضغط على زر البحث لعرض كافة النتائج في صفحة مستقلة.</p>
            </div>
          `;
        }
      }
    }

    renderResults(query, querySuggestions, matchedCollections, products, totalMatchesCount) {
      this.highlightedIndex = -1;
      this.interactiveElements = [];

      if (products.length === 0 && matchedCollections.length === 0) {
        this.container.innerHTML = `
          <div class="fudge-smart-search__empty">
            <div class="fudge-smart-search__empty-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
            </div>
            <h4 class="fudge-smart-search__empty-title">لم نجد نتائج مطابقة لـ «${this.escapeHtml(query)}»</h4>
            <p class="fudge-smart-search__empty-desc">جرّب كتابة اسم القطعة بشكل عام (مثال: فستان، بلوزة، بنطلون، حذاء، بيجامة) أو تصفح الأقسام الرئيسية.</p>
            <a href="/collections/all" class="fudge-smart-search__empty-btn">تصفح جميع المنتجات</a>
          </div>
        `;
        return;
      }

      let html = '<div class="fudge-smart-search-scroll">';

      // Section 1: Query Suggestions (if any)
      if (querySuggestions.length > 0) {
        html += `
          <div class="fudge-smart-search__section">
            <div class="fudge-smart-search__section-header">
              <span class="fudge-smart-search__section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                اقتراحات البحث
              </span>
            </div>
            <ul class="fudge-smart-search__queries-list">
        `;
        querySuggestions.forEach((qText) => {
          html += `
            <li>
              <button type="button" class="fudge-smart-search__query-pill js-fudge-query-pill" data-query="${this.escapeHtml(qText)}">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                <span>${this.escapeHtml(qText)}</span>
              </button>
            </li>
          `;
        });
        html += `
            </ul>
          </div>
          <div class="fudge-smart-search__divider"></div>
        `;
      }

      // Section 2: Matched Collections (if any)
      if (matchedCollections.length > 0) {
        html += `
          <div class="fudge-smart-search__section">
            <div class="fudge-smart-search__section-header">
              <span class="fudge-smart-search__section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                أقسام المتجر ذات الصلة
              </span>
            </div>
            <ul class="fudge-smart-search__collections-list">
        `;
        matchedCollections.forEach((col) => {
          const colInfo = getCollectionDisplayInfo(col);
          html += `
            <li>
              <a href="/collections/${col.handle}" class="fudge-smart-search__collection-item js-fudge-collection-item">
                <div class="fudge-smart-search__collection-info">
                  <span class="fudge-smart-search__collection-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                  </span>
                  <div class="fudge-smart-search__collection-text-group">
                    <span class="fudge-smart-search__collection-title">${this.escapeHtml(colInfo.title)}</span>
                    ${colInfo.parent ? `<span class="fudge-smart-search__collection-parent">تتبع لـ: ${this.escapeHtml(colInfo.parent)}</span>` : ''}
                  </div>
                </div>
                <span class="fudge-smart-search__collection-badge">${this.escapeHtml(colInfo.label)}</span>
              </a>
            </li>
          `;
        });
        html += `
            </ul>
          </div>
          <div class="fudge-smart-search__divider"></div>
        `;
      }

      // Section 3: Matched Products
      if (products.length > 0) {
        html += `
          <div class="fudge-smart-search__section">
            <div class="fudge-smart-search__section-header">
              <span class="fudge-smart-search__section-title">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
                المنتجات الأكثر تطابقاً
              </span>
            </div>
            <ul class="fudge-smart-search__products-list">
        `;
        products.forEach((prod) => {
          const imgUrl = prod.images?.[0]?.src || 'https://cdn.shopify.com/s/files/1/0993/8881/7592/files/fudge-hekaya-outlet-logo.png?v=1793714402';
          const price = prod.variants?.[0]?.price || 0;
          const isMixOffer = prod._isMixOffer;
          const isSoldOut = prod.variants && prod.variants.every(v => v.available === false);

          html += `
            <li>
              <a href="/products/${prod.handle}" class="fudge-smart-search__product-item js-fudge-product-item" data-handle="${prod.handle}">
                <div class="fudge-smart-search__product-img-wrap">
                  <img src="${imgUrl}" alt="${this.escapeHtml(prod.title)}" class="fudge-smart-search__product-img" width="52" height="52" loading="lazy">
                </div>
                <div class="fudge-smart-search__product-details">
                  <span class="fudge-smart-search__product-title">${this.escapeHtml(prod.title)}</span>
                  <div class="fudge-smart-search__product-meta">
                    <span class="fudge-smart-search__product-price">${formatJOD(price)}</span>
                    ${isMixOffer ? '<span class="fudge-smart-search__offer-badge">عرض 2 بـ 15 د.أ</span>' : ''}
                    ${isSoldOut ? '<span class="fudge-smart-search__soldout-badge">نفدت الكمية</span>' : ''}
                  </div>
                </div>
                <span class="fudge-smart-search__product-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" width="16" height="16"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                </span>
              </a>
            </li>
          `;
        });
        html += `
            </ul>
          </div>
        `;
      }

      html += '</div>'; // close .fudge-smart-search-scroll

      // Section 4: Footer - View All
      const searchUrl = `/search?type=product&options%5Bprefix%5D=last&q=${encodeURIComponent(query)}`;
      html += `
        <div class="fudge-smart-search__footer">
          <a href="${searchUrl}" class="fudge-smart-search__view-all-btn js-fudge-view-all-btn">
            <span>عرض كافة نتائج البحث لـ «${this.escapeHtml(query)}» (${totalMatchesCount} منتج)</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </a>
        </div>
      `;

      this.container.innerHTML = html;

      // Collect interactive elements for keyboard navigation
      const interactiveEls = this.container.querySelectorAll(
        '.js-fudge-query-pill, .js-fudge-collection-item, .js-fudge-product-item, .js-fudge-view-all-btn'
      );
      this.interactiveElements = Array.from(interactiveEls);

      // Bind events for query pills
      this.container.querySelectorAll('.js-fudge-query-pill').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const qText = btn.getAttribute('data-query');
          if (qText) {
            this.input.value = qText;
            if (this.clearBtn) this.clearBtn.style.display = 'block';
            this.executeSearch(qText);
          }
        });
      });

      // Bind events for product links to save recent search
      this.container.querySelectorAll('.js-fudge-product-item, .js-fudge-collection-item, .js-fudge-view-all-btn').forEach(link => {
        link.addEventListener('click', () => {
          if (typeof window.__hekayaSaveRecentSearch === 'function') {
            window.__hekayaSaveRecentSearch(query);
          }
        });
      });
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  // --- 5. Export to Window & Global Initializer ---
  window.HekayaSmartSearch = HekayaSmartSearch;
  window.hekayaNormalizeArabic = normalizeArabic;
  window.hekayaLoadCatalog = loadCatalog;
  window.hekayaLoadCollections = loadCollections;
  window.hekayaRankProducts = rankProducts;

})();
